// Olivium Governance — contextual action catalog (pure).
//
// Realizes the packet's "Not Read-only Action Standard": every record offers a
// universal action set plus module-specific governed transitions. Each governed
// action is a reducer command spec; the workspace renders it as a button that
// is disabled — WITH A VISIBLE REASON — when the effective role cannot perform
// it. No dead buttons: an action is either performable or explained.

import type { GovModule } from './governanceCards'
import type { GovAction, GovRecord, Severity, StatusKind } from './governanceModel'

export interface GovModuleAction {
  key: string
  label: string
  action: GovAction
  /** patch applied to the target record when performed */
  patch: Partial<GovRecord>
  severity?: Severity
  /** marks a destructive/denied transition (drives red styling + risk raise) */
  denied?: boolean
  /** primary action floats to the header; the rest live in the overflow menu */
  primary?: boolean
}

function status(kind: StatusKind, label: string): Partial<GovRecord> {
  return { statusKind: kind, status: label }
}

const AUDIT: GovModuleAction[] = [
  { key: 'verify', label: 'Verify', action: 'verify', patch: status('verified', 'Verified'), primary: true },
  { key: 'escalate', label: 'Escalate to risk', action: 'escalate', patch: {}, severity: 'high' },
]

const INTEGRITY: GovModuleAction[] = [
  { key: 'verify', label: 'Verify chain', action: 'verify', patch: status('verified', 'Verified'), primary: true },
  { key: 'escalate', label: 'Flag mismatch', action: 'escalate', patch: status('denied', 'Mismatch'), severity: 'high', denied: true },
]

const RECEIPTS: GovModuleAction[] = [
  { key: 'verify', label: 'Verify receipt', action: 'verify', patch: status('verified', 'Verified'), primary: true },
  { key: 'bundle', label: 'Bundle evidence', action: 'add_update', patch: { fields: { bundled: 'true' } } as Partial<GovRecord> },
]

const RISKS: GovModuleAction[] = [
  { key: 'acknowledge', label: 'Acknowledge', action: 'acknowledge', patch: status('attention', 'Acknowledged'), primary: true },
  { key: 'mitigate', label: 'Mitigate', action: 'mitigate', patch: status('attention', 'Mitigating'), severity: 'medium' },
  { key: 'resolve', label: 'Resolve', action: 'resolve', patch: status('verified', 'Resolved') },
  { key: 'escalate', label: 'Escalate', action: 'escalate', patch: status('denied', 'Escalated'), severity: 'high', denied: true },
]

const ACCESS_LOGS: GovModuleAction[] = [
  { key: 'trust_device', label: 'Trust device', action: 'trust_device', patch: status('verified', 'Trusted'), primary: true },
  { key: 'challenge', label: 'Challenge', action: 'challenge', patch: status('attention', 'Challenged'), severity: 'medium' },
  { key: 'suspend', label: 'Suspend', action: 'suspend', patch: status('attention', 'Suspended'), severity: 'medium' },
  { key: 'terminate', label: 'Terminate', action: 'terminate', patch: status('denied', 'Terminated'), severity: 'high', denied: true },
]

const SECURE_SHARE: GovModuleAction[] = [
  { key: 'extend', label: 'Extend', action: 'extend', patch: status('secure', 'Extended'), primary: true },
  { key: 'edit_constraints', label: 'Edit constraints', action: 'edit_constraints', patch: {} },
  { key: 'revoke', label: 'Revoke share', action: 'revoke', patch: status('denied', 'Revoked'), severity: 'high', denied: true },
]

const PERMISSION_REVIEW: GovModuleAction[] = [
  { key: 'approve', label: 'Approve', action: 'approve', patch: status('verified', 'Approved'), primary: true },
  { key: 'request_changes', label: 'Request changes', action: 'request_changes', patch: status('attention', 'Changes requested'), severity: 'medium' },
  { key: 'deny', label: 'Deny', action: 'deny', patch: status('denied', 'Denied'), severity: 'high', denied: true },
]

const BY_MODULE: Record<GovModule, GovModuleAction[]> = {
  audit: AUDIT,
  integrity: INTEGRITY,
  receipts: RECEIPTS,
  risks: RISKS,
  'access-logs': ACCESS_LOGS,
  'secure-share': SECURE_SHARE,
  'permission-review': PERMISSION_REVIEW,
}

export function moduleActions(module: GovModule): GovModuleAction[] {
  return BY_MODULE[module]
}

/** The universal, always-present record actions (from the action standard). */
export const UNIVERSAL_ACTIONS: readonly { key: string; label: string }[] = [
  { key: 'comment', label: 'Comment' },
  { key: 'add_update', label: 'Add update' },
  { key: 'assign', label: 'Assign' },
  { key: 'add_reviewer', label: 'Add reviewer' },
  { key: 'watch', label: 'Watch' },
  { key: 'share', label: 'Share' },
  { key: 'copy_link', label: 'Copy link' },
  { key: 'create_related', label: 'Create related' },
] as const
